import { ArrowDownWideNarrow, ArrowUpNarrowWide } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import type { FilterOption } from './define'

export interface SortControlProps<TField extends string> {
    fields: readonly FilterOption<TField>[]
    field: TField
    direction: 'asc' | 'desc'
    onChange: (next: { field: TField; direction: 'asc' | 'desc' }) => void
    className?: string
}

/**
 * For lists where the API sorts on fields that are not table columns. Where a field
 * does have a column, the column header is the better control and this is redundant.
 */
export function SortControl<TField extends string>({
    fields,
    field,
    direction,
    onChange,
    className
}: SortControlProps<TField>) {
    const DirectionIcon = direction === 'asc' ? ArrowUpNarrowWide : ArrowDownWideNarrow
    const nextDirection = direction === 'asc' ? 'desc' : 'asc'

    return (
        // Two controls a hair apart: a cluster, so both grow on a coarse pointer and the
        // gap grows with them. 128px + 6px + 36px = 170px, inside a 360px screen.
        <div className={cn('flex items-center gap-1 pointer-coarse:gap-1.5', className)}>
            <Select value={field} onValueChange={value => onChange({ field: value as TField, direction })}>
                <SelectTrigger size="sm" aria-label="Sort by" className="w-auto min-w-32 pointer-coarse:h-9">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {fields.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => onChange({ field, direction: nextDirection })}
                        aria-label={`Sort ${direction === 'asc' ? 'ascending' : 'descending'}, switch to ${nextDirection === 'asc' ? 'ascending' : 'descending'}`}
                        // The tooltip never opens on touch, but it only repeats what the
                        // aria-label already says, so nothing is unreachable there.
                        className="pointer-coarse:size-9">
                        <DirectionIcon />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>{direction === 'asc' ? 'Ascending' : 'Descending'}</TooltipContent>
            </Tooltip>
        </div>
    )
}
